import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSecurityMaster } from "../../context/SecurityMasterContext";
import { useMarketIndicators } from "../../context/MarketIndicatorContext";
import { useWatchlist } from "../../context/WatchlistContext";
import { useIndicators } from "../../context/IndicatorContext";

// import { useStockName } from "../../context/StockNameContext";
import { useMoreStockContext } from "../../context/MoreStockContext";
import { useMarketToggle } from "../../context/MarketContext";
import { useAuth } from "../../context/AuthNewContext";
import Gauge from "../Gauge/Gauge";
import { FaSpinner } from "react-icons/fa6";
import { FiRefreshCw } from "react-icons/fi";
import { useAdmin } from "../../context/AdminContext";
import { useVariant } from "../../hooks/useVariant";
import { useAIChat } from "../../context/AIChatContext";
import { FiStar } from "react-icons/fi"; // outline
import { useMarket } from "../../context/MarketDhanContext";
import { useChartIndicator } from "../../context/ChartIndicatorContext";
import { FaStar, FaRegStar } from "react-icons/fa"; // filled
import { TOOLTIPS } from "../../helper/tooltilData";

import UpgradeModal from "../UpgradeModal/UpgradeModal";
import { useStockSnapshot } from "../../context/StockSnapshotContext";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  ComposedChart,
} from "recharts";

import { useOption } from "../../context/OptionContext";

import RangeBarSVG from "../RangeSvg/RangeBarSVG";
import ChatPanel from "../ChatPanel/ChatPanel";
import AIFloatButton from "../AIFloatButton/AIFloatButton";
import Chat from "../Chat/Chat";
import { usePdf } from "../../context/PdfContext";
import ChatPanel2 from "../ChatPanel2/ChatPanel2";
import CandleChart from "../CandleChart/CandleChart";
import { useMarketSnapshot } from "../../context/MarketSnapshotContext";
import { useTechnicalSnapshot } from "../../context/TechnicalSnapshotContext";
import SmartMoney from "../SmartMoney/SmartMoney";
import "./StockDetailPage.css";

const runTradingAnalysis = ({
  ltp,
  vwap,
  prevClose,
  prevVWAP,
  volume,
  avgVolume,
  r1,
  s1,
  rLabel = "R1",
  sLabel = "S1",
  position,
}) => {
  if (!ltp || !vwap) return {};

  // ===============================
  // ⚙️ CONFIG
  // ===============================
  const ENTRY_ZONE_PCT = 0.002;
  const STOP_BUFFER = 0.001;
  const TARGET_PCT = 0.005;

  let entry = {};
  let signal = null;
  let exit = null;
  let newPosition = position;

  const entryZoneUpper = vwap * (1 + ENTRY_ZONE_PCT);

  const isNearVWAP = ltp >= vwap && ltp <= entryZoneUpper;
  const isReclaim = prevClose < prevVWAP && ltp > vwap;
  const isHighVolume = !volume || !avgVolume || volume > avgVolume * 0.8;

  const isAboveVWAP = ltp > vwap;

  if (!position?.inPosition) {
    if (isReclaim && isHighVolume) {
      entry = {
        label: "VWAP Reclaim Buy",
        msg: "Strong breakout above VWAP",
        type: "buy",
      };

      signal = `Bullish (Reclaim) @ ₹${ltp}`;

      if (!position?.inPosition) {
        newPosition = {
          inPosition: true,
          entryPrice: Number(ltp.toFixed(2)),
        };
      } else {
        newPosition = position;
      }
    } else if (isNearVWAP) {
      entry = {
        label: "Pullback Buy",
        msg: "VWAP Support Zone",
        type: "buy",
      };

      signal = `Bullish (Pullback) @ ₹${ltp}`;

      if (!position?.inPosition) {
        newPosition = {
          inPosition: true,
          entryPrice: Number(ltp.toFixed(2)),
        };
      } else {
        newPosition = position;
      }
    } else {
      entry = {
        label: "No Trade",
        msg: "Wait for VWAP setup",
        type: "neutral",
      };
    }
  }

  // ===============================
  // EXIT
  // ===============================
  else if (position?.inPosition) {
    const entryPrice = position.entryPrice;

    const target = entryPrice * (1 + TARGET_PCT);
    const stopLoss = vwap * (1 - STOP_BUFFER);

    if (ltp >= target) {
      exit = `Target Hit 🚀 SELL @ ₹${ltp}`;

      if (position?.inPosition) {
        newPosition = {
          inPosition: false,
          entryPrice: null,
        };
      } else {
        newPosition = position;
      }
    } else if (ltp < stopLoss) {
      exit = `VWAP Breakdown ❌ SELL @ ₹${ltp}`;

      if (position?.inPosition) {
        newPosition = {
          inPosition: false,
          entryPrice: null,
        };
      } else {
        newPosition = position;
      }
    }

    entry = {
      label: "In Trade",
      msg: `Entry ₹${entryPrice} | LTP ₹${ltp}`,
      type: "active",
    };
  }

  // ===============================
  // DEAD BLOCK SAFE (NOT REMOVED)
  // ===============================
  else if (isAboveVWAP) {
    entry = {
      label: "Trend Bullish",
      msg: "Price holding above VWAP",
      type: "buy",
    };

    if (!signal) {
      signal = `Bullish (Trend) @ ₹${ltp}`;
    }

    newPosition = position; // prevent loop
  } else {
    entry = {
      label: "No Trade",
      msg: "Wait for VWAP setup",
      type: "neutral",
    };
  }

  if (!signal && !position?.inPosition) {
    if (ltp > vwap) {
      signal = `Bullish (Fallback) @ ₹${ltp}`;
      entry = {
        label: "Fallback Bullish",
        msg: "Above VWAP (fallback)",
        type: "buy",
      };

      newPosition = position;
    } else if (ltp < vwap) {
      signal = `Bearish (Fallback) @ ₹${ltp}`;
      entry = {
        label: "Fallback Bearish",
        msg: "Below VWAP (fallback)",
        type: "sell",
      };

      newPosition = position;
    }
  }

  // ===============================
  // HOLD STATE
  // ===============================
  if (position?.inPosition && !exit) {
    exit = `HOLD → LTP ₹${ltp}`;
  }

  // ===============================
  // TARGETS
  // ===============================
  const targets = {
    primary:
      ltp > vwap ? { label: rLabel, value: r1 } : { label: sLabel, value: s1 },
  };

  // ===============================
  // STOP LOSS DISPLAY
  // ===============================
  const stopLossDisplay =
    ltp > vwap
      ? (vwap * (1 - STOP_BUFFER)).toFixed(2)
      : (vwap * (1 + STOP_BUFFER)).toFixed(2);

  // ===============================
  // FINAL SIGNAL SAFETY
  // ===============================
  if (!signal && position?.inPosition) {
    signal = "IN TRADE";
  }

  let pythonSignal = null;
  let pythonTrend = null;

  const ENTRY_ZONE_PCT_PY = 0.002; // Widened to 0.2% to match main strategy
  const TARGET_PCT_PY = 0.01; // 1.0% target gain
  const entryZoneUpperPy = vwap * (1 + ENTRY_ZONE_PCT_PY);

  if (!position?.inPosition) {
    if (ltp >= vwap && ltp <= entryZoneUpperPy) {
      pythonSignal = `
       
         BUY at ₹${ltp}, VWAP ₹${vwap.toFixed(2)}`;
      pythonTrend = "BULLISH ENTRY";
      // setTrend(pythonTrend);
    } else if (ltp > vwap) {
      // ADDED: Fallback signal so it's not null when price is high
      pythonSignal = `
    
        LTP ₹${ltp} is above VWAP`;
      pythonTrend = "TRENDING BULLISH ";
      // setTrend(pythonTrend);
    }
  } else if (position?.inPosition) {
    const entryPrice = position.entryPrice;

    if (ltp >= entryPrice * (1 + TARGET_PCT_PY)) {
      pythonSignal = `
       SELL at ₹${ltp}, Gain: 1%`;
      pythonTrend = "EXIT PROFIT";
      // setTrend(pythonTrend);
    } else if (ltp < vwap) {
      pythonSignal = `LOSS BEARISH at ₹${ltp}, Below VWAP`;
      pythonTrend = "EXIT STOP";
      // setTrend(pythonTrend);
    }
  }

  // Trader Note logic for display
  const traderNote = position?.inPosition
    ? `IN TRADE → Entry ₹${position.entryPrice} | Target ₹${(
        position.entryPrice *
        (1 + TARGET_PCT_PY)
      ).toFixed(2)}`
    : pythonSignal || `WAIT → VWAP ₹${vwap.toFixed(2)}`;
  console.log(
    "Final values",
    signal,
    exit,
    targets,
    stopLossDisplay,
    traderNote,
    newPosition,
    pythonSignal,
    pythonTrend,
  );
  return {
    entry,
    signal,
    exit,
    targets,
    stopLoss: stopLossDisplay,
    traderNote,
    newPosition,
    pythonSignal,
    pythonTrend, // ✅ NEW
  };
};
const StockDetailPage = () => {
  // const {
  //   marketSnapshot,
  //   marketLoading,
  //   marketError,
  //   getMarketSnapshot,
  //   resetMarketSnapshot,
  //   initializeMarketSnapshot,
  // } = useMarketSnapshot();
  const {
    stockDetail,
    stockDetailLoading,
    stockDetailError,
    getStockDetailSnapshot,
    resetStockDetail,
    getStockFundamentals,
    fundamentals,
    fundamentalsError,
    fundamentalsLoading,
  } = useStockSnapshot();
  const { downloadStockPDF } = usePdf();
  // const { fetchBySymbol, pcr } = useOption();
  // const {
  //   getTechnicalSnapshot,
  //   technicalSnapshot,
  //   resetTechnicalSnapshot,
  //   initializeTechnicalSnapshot,
  // } = useTechnicalSnapshot();
  const [crossovers, setCrossovers] = useState([]);
  const [liveReady, setLiveReady] = useState(false);
  const { market } = useMarketToggle();
  const [timeFrame, setTimeFrame] = useState("min");
  const [resistTimeFrame, setResistTimeFrame] = useState("day");
 
  const [position, setPosition] = useState({
    inPosition: false,
    entryPrice: null,
  });
  const [smartMode, setSmartMode] = useState("intraday");
  const [lastUTSignal, setLastUTSignal] = useState(null);
  const [interval, setInterval] = useState("1d");
  // const {
  //   getTargetPrice,
  //   targetPrice,
  //   getNameBySymbol,
  //   stockName,
  //   resetTargetPrice,
  // } = useMarketIndicators();
  const navigate = useNavigate();
  const {
    addToWatchlist,
    removeFromWatchlist,
    resetWatchlist,
    fetchWatchlist,
    watchlist,
    isInWatchlist,
  } = useWatchlist();
  const variant = useVariant();
  const { chatOpen } = useAIChat();
  const { symbol, securityId: paramSecurityId } = useParams();
  const [visibleCount, setVisibleCount] = useState(8);

  console.count("StockTest Render");
  // useEffect(() => {
  //   if (!symbol) return;

  //   const loadSnapshot = async () => {
  //     resetMarketSnapshot(); // clear previous stock data

  //     resetTargetPrice();
  //     await getMarketSnapshot(symbol);
  //     await getTechnicalSnapshot(symbol);
  //     await getTargetPrice(symbol);
  //     await getNameBySymbol(symbol);
  //   };

  //   loadSnapshot();
  // }, [symbol]);
  // useEffect(() => {
  //   if (!symbol) return;

  //   const loadSnapshot = async () => {
  //     setLiveReady(false);
  //     resetStockDetail();
  //     // resetMarketSnapshot();
  //     resetTargetPrice();
  //     // resetTechnicalSnapshot(); // add in context
  //     // resetStockName();         // add in context
  //     setCrossovers([]);

  //     await Promise.all([
  //       getStockDetailSnapshot(symbol),
  //       // getMarketSnapshot(symbol),
  //       // getTechnicalSnapshot(symbol),
  //       getTargetPrice(symbol, market),
  //       getNameBySymbol(symbol),
  //     ]);

  //     setLiveReady(true);
  //   };

  //   loadSnapshot();
  // }, [symbol]);

  //earlier working code
  // useEffect(() => {
  //   if (!symbol) return;

  //   let cancelled = false;

  //   const loadSnapshot = async () => {
  //     setLiveReady(false);
  //     resetStockDetail();

  //     // resetTargetPrice();
  //     setCrossovers([]);

  //     const stockData = await getStockDetailSnapshot(symbol, paramSecurityId);
  //     const fundamentalData = await getStockFundamentals(symbol);

  //     if (!cancelled && stockData) {
  //       setLiveReady(true);
  //     }
  //     // fetchBySymbol(symbol);
  //     // run these after page is ready
  //     // getTargetPrice(symbol, market);
  //     // getNameBySymbol(symbol);
  //   };

  //   loadSnapshot();

  //   return () => {
  //     cancelled = true;
  //   };
  // }, [symbol]);
  useEffect(() => {
    if (!symbol) return;

    let cancelled = false;

    const loadSnapshot = async () => {
      setLiveReady(false);
      resetStockDetail();
      setCrossovers([]);

      // 🔥 PARALLEL — both fire at same time, page shows when snapshot arrives
      const [stockData] = await Promise.all([
        getStockDetailSnapshot(symbol, paramSecurityId),
        getStockFundamentals(symbol),
      ]);

      if (!cancelled && stockData) {
        setLiveReady(true);
      }
    };

    loadSnapshot();

    return () => {
      cancelled = true;
    };
  }, [symbol]);
  console.log("Stock Snapshot", stockDetail);
  const marketSnapshot = stockDetail?.marketSnapshot;
  const technicalSnapshot = stockDetail?.technicalSnapshot;
  const stockName = stockDetail?.stockData;
  const targetPrice = fundamentals?.targetPrice;
  console.log("Fundamentals", fundamentals);
  // console.log("pcr", pcr);
  // useEffect(() => {
  //   if (!symbol) return;

  //   const loadSnapshot = async () => {
  //     setLiveReady(false);
  //     resetMarketSnapshot();
  //     resetTargetPrice();
  //     resetTechnicalSnapshot();
  //     setCrossovers([]);

  //     const t0 = Date.now();

  //     const [marketData, technicalData] = await Promise.all([
  //       getMarketSnapshot(symbol),
  //       getTechnicalSnapshot(symbol),
  //       getTargetPrice(symbol, market),
  //       getNameBySymbol(symbol),
  //     ]);

  //     console.log(`⏱️ DB fetch: ${Date.now() - t0}ms`);
  //     console.log(`📦 market: ${!!marketData}, technical: ${!!technicalData}`);

  //     if (marketData || technicalData) {
  //       setLiveReady(true);
  //     }

  //     if (!marketData && paramSecurityId) {
  //       const t1 = Date.now();
  //       initializeMarketSnapshot({
  //         symbol,
  //         securityId: Number(paramSecurityId),
  //       })
  //         .then(async () => {
  //           console.log(`⏱️ Market init: ${Date.now() - t1}ms`);
  //           await getMarketSnapshot(symbol);
  //           setLiveReady(true);
  //         })
  //         .catch(console.error);
  //     }

  //     if (!technicalData && paramSecurityId) {
  //       const t2 = Date.now();
  //       initializeTechnicalSnapshot({
  //         symbol,
  //         securityId: Number(paramSecurityId),
  //       })
  //         .then(async () => {
  //           console.log(`⏱️ Technical init: ${Date.now() - t2}ms`);
  //           await getTechnicalSnapshot(symbol);
  //         })
  //         .catch(console.error);
  //     }

  //     if (!marketData && !technicalData && !paramSecurityId) {
  //       setLiveReady(true);
  //     }
  //   };

  //   loadSnapshot();
  // }, [symbol]);
  console.log("Market Snapshot", marketSnapshot);
  console.log("Technical Snapshot", technicalSnapshot);
  console.log("Target Price", targetPrice);
  // console.log("StockName ", stockName);
  const quote = marketSnapshot?.quote?.data;
  const isActive = isInWatchlist(symbol);
  console.log("isActive", isActive);
  const last_price = quote?.last_price;
  const ohlc = quote?.ohlc;
  const close = ohlc?.close;
  const open = ohlc?.open;
  const high = ohlc?.high;
  const low = ohlc?.low;
  const percent_change = (((last_price - close) / close) * 100).toFixed(2);

  const intraday = marketSnapshot?.intraday;
  const vwap = intraday?.vwap;
  console.log("toye of vwap", typeof vwap);
  const bb_signal = intraday?.bbawe_signal;
  const ut_signal = intraday?.ut_signal;
  console.log("ut_signal", ut_signal);
  const vwap_signal = intraday?.vwap_signal;
  const rsi_macd_signal = intraday?.prediction;
  const diff = vwap && last_price ? last_price - vwap : 0;
  const percent = vwap ? ((diff / vwap) * 100).toFixed(2) : 0;
  // const rawPrice = targetPrice?.data?.target_price;
  // const target_price = rawPrice
  //   ? Number(String(rawPrice).replace(/,/g, ""))
  //   : 0;

  // const rawLowPrice = targetPrice?.data?.target_low;

  // ✅ only use targetPrice if it matches current symbol

  const rawPrice =
    targetPrice?.symbol === symbol ? targetPrice?.target_price : null;
  const target_price = rawPrice
    ? Number(String(rawPrice).replace(/,/g, ""))
    : 0;

  const rawLowPrice =
    targetPrice?.symbol === symbol ? targetPrice?.target_low : null;

  const rawHighPrice =
    targetPrice?.symbol === symbol ? targetPrice?.target_high : null;
  const target_low = rawLowPrice
    ? Number(String(rawLowPrice).replace(/,/g, ""))
    : 0;

  // const rawHighPrice = targetPrice?.data?.target_high;
  const target_high = rawHighPrice
    ? Number(String(rawHighPrice).replace(/,/g, ""))
    : 0;
  console.log("Target Price", target_price);

  const projectChange =
    target_price &&
    (((target_price - quote?.last_price) / quote?.last_price) * 100).toFixed(2);
  console.log("ProjectedChange", projectChange);
  const maData = technicalSnapshot?.ma_data?.movingAverages || [];

  // resDaily
  const resDaily = technicalSnapshot?.resDaily;
  //resWeekly
  const resWeekly = technicalSnapshot?.resWeekly;
  const resistanceDataToRender =
    resistTimeFrame === "day" ? resDaily : resWeekly;
  const ltp = quote?.last_price;

  // const pivotLevels = resistanceDataToRender?.classic;

  // const resistanceLevels = [
  //   pivotLevels?.R1,
  //   pivotLevels?.R2,
  //   pivotLevels?.R3,
  // ].filter((v) => typeof v === "number" && v > ltp);

  // const supportLevels = [
  //   pivotLevels?.S1,
  //   pivotLevels?.S2,
  //   pivotLevels?.S3,
  // ].filter((v) => typeof v === "number" && v < ltp);

  // const nearestResistance = resistanceLevels.length
  //   ? { value: Math.min(...resistanceLevels) }
  //   : null;

  // const nearestSupport = supportLevels.length
  //   ? { value: Math.max(...supportLevels) }
  //   : null;
  const { nearestResistance, nearestSupport, pivotLevels } = useMemo(() => {
    const pivotLevels = resistanceDataToRender?.classic;
    if (!pivotLevels || !ltp) {
      return {
        nearestResistance: null,
        nearestSupport: null,
        pivotLevels: null,
      };
    }

    const resistanceLevels = [
      pivotLevels.R1,
      pivotLevels.R2,
      pivotLevels.R3,
    ].filter((v) => typeof v === "number" && v > ltp);

    const supportLevels = [
      pivotLevels.S1,
      pivotLevels.S2,
      pivotLevels.S3,
    ].filter((v) => typeof v === "number" && v < ltp);

    return {
      pivotLevels,
      nearestResistance: resistanceLevels.length
        ? { value: Math.min(...resistanceLevels) }
        : null,
      nearestSupport: supportLevels.length
        ? { value: Math.max(...supportLevels) }
        : null,
    };
  }, [resistanceDataToRender, ltp]);
  const latestVWAP = Number(vwap);
  const ltpValue = Number(last_price);

  const prevClose = technicalSnapshot?.ohcl?.close;

  // const analysis = useMemo(() => {
  //   if (!ltpValue || !latestVWAP) return null;

  //   const diff = Math.abs(ltpValue - latestVWAP);
  //   const buffer = 0.2;
  //   const isPullbackZone = diff > 0.3 && diff < 1.5;

  //   const stopLoss =
  //     ltpValue > latestVWAP
  //       ? (latestVWAP - buffer).toFixed(2)
  //       : (latestVWAP + buffer).toFixed(2);

  //   const r1 = nearestResistance?.value;
  //   const s1 = nearestSupport?.value;

  //   let entry;

  //   if (diff < 0.5) {
  //     entry = {
  //       label: "Neutral",
  //       msg: "Consolidating near VWAP",
  //       type: "neutral",
  //     };
  //   } else if (ltpValue > latestVWAP) {
  //     entry = {
  //       label: isPullbackZone ? "Pullback Buy" : "Bullish Breakout",
  //       msg: isPullbackZone
  //         ? "Institutional Fair Price Zone"
  //         : "Momentum Active",
  //       type: "buy",
  //     };
  //   } else {
  //     entry = {
  //       label: isPullbackZone ? "Pullback Sell" : "Bearish Breakdown",
  //       msg: isPullbackZone
  //         ? "Institutional Sell Zone"
  //         : "Short Momentum Active",
  //       type: "sell",
  //     };
  //   }

  //   return {
  //     entry,
  //     stopLoss,
  //     signal:
  //       ltpValue > latestVWAP ? "Bullish above VWAP" : "Bearish below VWAP",
  //     exit:
  //       ltpValue > latestVWAP
  //         ? `Book near ₹${r1?.toFixed(2)}`
  //         : `Book near ₹${s1?.toFixed(2)}`,
  //     pythonTrend:
  //       ltpValue > latestVWAP ? "VWAP Trend: Bullish" : "VWAP Trend: Bearish",
  //     traderNote:
  //       ltpValue > latestVWAP
  //         ? `Buy above ₹${ltpValue}, Target ₹${r1?.toFixed(2)}, SL ₹${stopLoss}`
  //         : `Sell below ₹${ltpValue}, Target ₹${s1?.toFixed(2)}, SL ₹${stopLoss}`,
  //   };
  // }, [ltpValue, latestVWAP, nearestResistance, nearestSupport]);
  // const runTradingAnalysis = ({
  //   ltp,
  //   vwap,
  //   prevClose,
  //   prevVWAP,
  //   volume,
  //   avgVolume,
  //   r1,
  //   s1,
  //   rLabel = "R1",
  //   sLabel = "S1",
  //   position,
  // }) => {
  //   if (!ltp || !vwap) return {};

  //   // ===============================
  //   // ⚙️ CONFIG
  //   // ===============================
  //   const ENTRY_ZONE_PCT = 0.002;
  //   const STOP_BUFFER = 0.001;
  //   const TARGET_PCT = 0.005;

  //   let entry = {};
  //   let signal = null;
  //   let exit = null;
  //   let newPosition = position;

  //   const entryZoneUpper = vwap * (1 + ENTRY_ZONE_PCT);

  //   const isNearVWAP = ltp >= vwap && ltp <= entryZoneUpper;
  //   const isReclaim = prevClose < prevVWAP && ltp > vwap;
  //   const isHighVolume = !volume || !avgVolume || volume > avgVolume * 0.8;

  //   const isAboveVWAP = ltp > vwap;

  //   if (!position?.inPosition) {
  //     if (isReclaim && isHighVolume) {
  //       entry = {
  //         label: "VWAP Reclaim Buy",
  //         msg: "Strong breakout above VWAP",
  //         type: "buy",
  //       };

  //       signal = `Bullish (Reclaim) @ ₹${ltp}`;

  //       if (!position?.inPosition) {
  //         newPosition = {
  //           inPosition: true,
  //           entryPrice: Number(ltp.toFixed(2)),
  //         };
  //       } else {
  //         newPosition = position;
  //       }
  //     } else if (isNearVWAP) {
  //       entry = {
  //         label: "Pullback Buy",
  //         msg: "VWAP Support Zone",
  //         type: "buy",
  //       };

  //       signal = `Bullish (Pullback) @ ₹${ltp}`;

  //       if (!position?.inPosition) {
  //         newPosition = {
  //           inPosition: true,
  //           entryPrice: Number(ltp.toFixed(2)),
  //         };
  //       } else {
  //         newPosition = position;
  //       }
  //     } else {
  //       entry = {
  //         label: "No Trade",
  //         msg: "Wait for VWAP setup",
  //         type: "neutral",
  //       };
  //     }
  //   }

  //   // ===============================
  //   // EXIT
  //   // ===============================
  //   else if (position?.inPosition) {
  //     const entryPrice = position.entryPrice;

  //     const target = entryPrice * (1 + TARGET_PCT);
  //     const stopLoss = vwap * (1 - STOP_BUFFER);

  //     if (ltp >= target) {
  //       exit = `Target Hit 🚀 SELL @ ₹${ltp}`;

  //       if (position?.inPosition) {
  //         newPosition = {
  //           inPosition: false,
  //           entryPrice: null,
  //         };
  //       } else {
  //         newPosition = position;
  //       }
  //     } else if (ltp < stopLoss) {
  //       exit = `VWAP Breakdown ❌ SELL @ ₹${ltp}`;

  //       if (position?.inPosition) {
  //         newPosition = {
  //           inPosition: false,
  //           entryPrice: null,
  //         };
  //       } else {
  //         newPosition = position;
  //       }
  //     }

  //     entry = {
  //       label: "In Trade",
  //       msg: `Entry ₹${entryPrice} | LTP ₹${ltp}`,
  //       type: "active",
  //     };
  //   }

  //   // ===============================
  //   // DEAD BLOCK SAFE (NOT REMOVED)
  //   // ===============================
  //   else if (isAboveVWAP) {
  //     entry = {
  //       label: "Trend Bullish",
  //       msg: "Price holding above VWAP",
  //       type: "buy",
  //     };

  //     if (!signal) {
  //       signal = `Bullish (Trend) @ ₹${ltp}`;
  //     }

  //     newPosition = position; // prevent loop
  //   } else {
  //     entry = {
  //       label: "No Trade",
  //       msg: "Wait for VWAP setup",
  //       type: "neutral",
  //     };
  //   }

  //   if (!signal && !position?.inPosition) {
  //     if (ltp > vwap) {
  //       signal = `Bullish (Fallback) @ ₹${ltp}`;
  //       entry = {
  //         label: "Fallback Bullish",
  //         msg: "Above VWAP (fallback)",
  //         type: "buy",
  //       };

  //       newPosition = position;
  //     } else if (ltp < vwap) {
  //       signal = `Bearish (Fallback) @ ₹${ltp}`;
  //       entry = {
  //         label: "Fallback Bearish",
  //         msg: "Below VWAP (fallback)",
  //         type: "sell",
  //       };

  //       newPosition = position;
  //     }
  //   }

  //   // ===============================
  //   // HOLD STATE
  //   // ===============================
  //   if (position?.inPosition && !exit) {
  //     exit = `HOLD → LTP ₹${ltp}`;
  //   }

  //   // ===============================
  //   // TARGETS
  //   // ===============================
  //   const targets = {
  //     primary:
  //       ltp > vwap
  //         ? { label: rLabel, value: r1 }
  //         : { label: sLabel, value: s1 },
  //   };

  //   // ===============================
  //   // STOP LOSS DISPLAY
  //   // ===============================
  //   const stopLossDisplay =
  //     ltp > vwap
  //       ? (vwap * (1 - STOP_BUFFER)).toFixed(2)
  //       : (vwap * (1 + STOP_BUFFER)).toFixed(2);

  //   // ===============================
  //   // FINAL SIGNAL SAFETY
  //   // ===============================
  //   if (!signal && position?.inPosition) {
  //     signal = "IN TRADE";
  //   }

  //   let pythonSignal = null;
  //   let pythonTrend = null;

  //   const ENTRY_ZONE_PCT_PY = 0.002; // Widened to 0.2% to match main strategy
  //   const TARGET_PCT_PY = 0.01; // 1.0% target gain
  //   const entryZoneUpperPy = vwap * (1 + ENTRY_ZONE_PCT_PY);

  //   if (!position?.inPosition) {
  //     if (ltp >= vwap && ltp <= entryZoneUpperPy) {
  //       pythonSignal = `

  //        BUY at ₹${ltp}, VWAP ₹${vwap.toFixed(2)}`;
  //       pythonTrend = "BULLISH ENTRY";
  //       // setTrend(pythonTrend);
  //     } else if (ltp > vwap) {
  //       // ADDED: Fallback signal so it's not null when price is high
  //       pythonSignal = `

  //       LTP ₹${ltp} is above VWAP`;
  //       pythonTrend = "TRENDING BULLISH ";
  //       // setTrend(pythonTrend);
  //     }
  //   } else if (position?.inPosition) {
  //     const entryPrice = position.entryPrice;

  //     if (ltp >= entryPrice * (1 + TARGET_PCT_PY)) {
  //       pythonSignal = `
  //      SELL at ₹${ltp}, Gain: 1%`;
  //       pythonTrend = "EXIT PROFIT";
  //       // setTrend(pythonTrend);
  //     } else if (ltp < vwap) {
  //       pythonSignal = `LOSS BEARISH at ₹${ltp}, Below VWAP`;
  //       pythonTrend = "EXIT STOP";
  //       // setTrend(pythonTrend);
  //     }
  //   }

  //   // Trader Note logic for display
  //   const traderNote = position?.inPosition
  //     ? `IN TRADE → Entry ₹${position.entryPrice} | Target ₹${(
  //         position.entryPrice *
  //         (1 + TARGET_PCT_PY)
  //       ).toFixed(2)}`
  //     : pythonSignal || `WAIT → VWAP ₹${vwap.toFixed(2)}`;
  //   console.log(
  //     "Final values",
  //     signal,
  //     exit,
  //     targets,
  //     stopLossDisplay,
  //     traderNote,
  //     newPosition,
  //     pythonSignal,
  //     pythonTrend,
  //   );
  //   return {
  //     entry,
  //     signal,
  //     exit,
  //     targets,
  //     stopLoss: stopLossDisplay,
  //     traderNote,
  //     newPosition,
  //     pythonSignal,
  //     pythonTrend, // ✅ NEW
  //   };
  // };
  // const analysis = useMemo(() => {
  //   if (!quote || !latestVWAP) return null;

  //   return runTradingAnalysis({
  //     ltp: quote?.last_price,
  //     vwap: latestVWAP,

  //     // 🔥 NEW (add these if available, else fallback)
  //     prevClose: prevClose || quote?.last_price,
  //     prevVWAP: latestVWAP,
  //     volume: quote?.volume ?? 0,
  //     avgVolume: 0,

  //     r1: nearestResistance?.value,
  //     s1: nearestSupport?.value,
  //     rLabel: nearestResistance?.key,
  //     sLabel: nearestSupport?.key,

  //     // 🔥 CRITICAL
  //     position,
  //   });
  // }, [
  //   quote?.last_price,
  //   latestVWAP,
  //   nearestResistance,
  //   nearestSupport,
  //   position, // 🔥 MUST ADD
  // ]);

  // Remove the position sync useEffect entirely and handle it inside useMemo
  const analysis = useMemo(() => {
    if (!quote || !latestVWAP) return null;

    const result = runTradingAnalysis({
      ltp: quote?.last_price,
      vwap: latestVWAP,
      prevClose: prevClose || quote?.last_price,
      prevVWAP: latestVWAP,
      volume: quote?.volume ?? 0,
      avgVolume: 0,
      r1: nearestResistance?.value,
      s1: nearestSupport?.value,
      rLabel: nearestResistance?.key,
      sLabel: nearestSupport?.key,
      position,
    });

    return result;
  }, [quote?.last_price, latestVWAP, nearestResistance, nearestSupport]);
  // 🔥 REMOVED position from deps — breaks the loop

  // Replace the sync useEffect with a one-time position setter
  useEffect(() => {
    if (!analysis?.newPosition) return;
    // Only update position on initial load or when price actually changes
    setPosition((prev) => {
      if (
        prev.inPosition === analysis.newPosition.inPosition &&
        prev.entryPrice === analysis.newPosition.entryPrice
      ) {
        return prev; // same — no re-render
      }
      return analysis.newPosition;
    });
  }, [quote?.last_price]); // ← only when price changes, not on every analysis

  useEffect(() => {
    if (!analysis?.newPosition) return;

    const prev = position || {};
    const next = analysis.newPosition || {};

    const isSame =
      prev.inPosition === next.inPosition &&
      Number(prev.entryPrice || 0).toFixed(2) ===
        Number(next.entryPrice || 0).toFixed(2);

    if (!isSame) {
      setPosition(next);
    }
  }, [analysis?.newPosition]);
  const getSignalTooltip = (signal) => {
    if (!signal) return [];

    if (signal.includes("Fallback")) {
      return [
        { id: 1, text: "Fallback Signal" },
        { id: 2, text: "Weak direction based on VWAP" },
        { id: 3, text: "No strong confirmation present" },
        { id: 4, text: "Use with caution" },
      ];
    }

    if (signal.includes("Reclaim")) {
      return [
        { id: 1, text: "VWAP Reclaim" },
        { id: 2, text: "Strong bullish signal" },
        { id: 3, text: "Price crossed above VWAP" },
      ];
    }

    if (signal.includes("Pullback")) {
      return [
        { id: 1, text: "VWAP Pullback" },
        { id: 2, text: "Medium strength signal" },
        { id: 3, text: "Price bouncing near VWAP" },
      ];
    }

    return [
      { id: 1, text: "Trading Signal" },
      { id: 2, text: "Based on strategy conditions" },
    ];
  };
  const InfoTooltip = ({ text }) => {
    if (!Array.isArray(text)) return null;

    return (
      <span className="table-tooltip-wrapper">
        <span style={{ color: "black" }}> ⓘ</span>
        <div className="table-tooltip-content">
          {text.map((item) => (
            <div key={item.id} className="tooltip-line">
              {item.text}
            </div>
          ))}
        </div>
      </span>
    );
  };
  // const InfoTooltip = ({ text }) => {
  //   if (!Array.isArray(text)) return null;

  //   return (
  //     <span className="table-tooltip-wrapper">
  //       <span className="tooltip-icon">ⓘ</span>

  //       <span className="table-tooltip-content">
  //         {text.map((item) => (
  //           <span key={item.id} className="tooltip-line">
  //             {item.text}
  //           </span>
  //         ))}
  //       </span>
  //     </span>
  //   );
  // };

  const levels = [
    { key: "R3", label: "Third Resistance" },
    { key: "R2", label: "Second Resistance" },
    { key: "R1", label: "First Resistance" },
    { key: "P", label: "Pivot" },
    { key: "S1", label: "First Support" },
    { key: "S2", label: "Second Support" },
    { key: "S3", label: "Third Support" },
  ];

  const getStatus = (value, ltp) => {
    if (!value || !ltp) return "—";

    if (value > ltp) return "Above LTP";
    if (value < ltp) return "Below LTP";

    return "At LTP";
  };

  const getPriority = (value, ltp) => {
    if (!value || !ltp) return 0;

    const diffPercent = Math.abs((value - ltp) / ltp) * 100;

    if (diffPercent <= 0.5) return 3;
    if (diffPercent <= 1.5) return 2;
    if (diffPercent <= 3) return 1;

    return 0;
  };

  const renderStars = (priority) => {
    if (priority === 3) return "⭐⭐⭐";
    if (priority === 2) return "⭐⭐";
    if (priority === 1) return "⭐";
    return "—";
  };

  const weekLow = quote?.["52_week_low"];
  const weekHigh = quote?.["52_week_high"];

  const buildMARows = (ltp, maData = []) => {
    if (!ltp || !Array.isArray(maData)) return [];

    return maData.map((ma) => {
      const smaTrend = ltp >= ma?.sma ? "Buy" : "Sell";
      const emaTrend = ltp >= ma?.ema ? "Buy" : "Sell";

      return {
        name: ma?.name || "-",
        smaValue: Number(ma?.sma || 0),
        emaValue: Number(ma?.ema || 0),
        smaTrend,
        emaTrend,
      };
    });
  };

  const buildMASummary = (rows = []) => {
    const finalBuy =
      rows.filter((r) => r.smaTrend === "Buy").length +
      rows.filter((r) => r.emaTrend === "Buy").length;

    const finalSell =
      rows.filter((r) => r.smaTrend === "Sell").length +
      rows.filter((r) => r.emaTrend === "Sell").length;

    return { finalBuy, finalSell };
  };
  const rows = useMemo(() => {
    if (!ltp || !maData?.length) return [];
    return buildMARows(ltp, maData);
  }, [ltp, maData]);

  // const summary = buildMASummary(rows);
  const summary = useMemo(() => buildMASummary(rows), [rows]);
  //ma data moving averages

  // depth from quotes

  const depth = quote?.depth;
  console.log("Depth from quote", depth);
  const buyQty = depth?.buy?.reduce((s, b) => s + b.quantity, 0);
  const sellQty = depth?.sell?.reduce((s, s1) => s + s1.quantity, 0);
  const buyOrders = depth?.buy?.reduce((s, b) => s + b.orders, 0);
  const sellOrders = depth?.sell?.reduce((s, s1) => s + s1.orders, 0);
  const totalBuyQty = depth?.buy?.reduce(
    (sum, b) => sum + (b.quantity || 0),
    0,
  );

  const totalSellQty = depth?.sell?.reduce(
    (sum, s) => sum + (s.quantity || 0),
    0,
  );

  const totalQty = totalBuyQty + totalSellQty;

  const buyPercent = totalQty ? (totalBuyQty / totalQty) * 100 : 0;

  const sellPercent = totalQty ? (totalSellQty / totalQty) * 100 : 0;

  //crossover

  const detectCrossovers = (data, priceData) => {
    const signals = [];

    for (let i = 1; i < data.length; i++) {
      const prev = data[i - 1];
      const curr = data[i];

      if (
        prev.MACD == null ||
        prev.signal == null ||
        curr.MACD == null ||
        curr.signal == null
      )
        continue;

      const price = priceData?.[i]?.close;

      // BUY crossover
      if (prev.MACD < prev.signal && curr.MACD > curr.signal) {
        signals.push({
          type: "BUY",
          date: curr.date,
          index: i,
          price,
          macd: curr.MACD,
          signal: curr.signal,
        });
      }

      // SELL crossover
      if (prev.MACD > prev.signal && curr.MACD < curr.signal) {
        signals.push({
          type: "SELL",
          date: curr.date,
          index: i,
          price,
          macd: curr.MACD,
          signal: curr.signal,
        });
      }
    }

    return signals;
  };

  const calculateTradePerformance = (signals, priceData) => {
    const trades = [];

    let activeTrade = null;

    for (let i = 0; i < signals.length; i++) {
      const signal = signals[i];

      // =========================
      // BUY ENTRY
      // =========================
      if (signal.type === "BUY" && !activeTrade) {
        activeTrade = {
          entryDate: signal.date,
          entryPrice: signal.price,

          highestPrice: signal.price,

          status: "OPEN",
        };
      }

      // =========================
      // TRACK MAX PROFIT
      // =========================
      if (activeTrade) {
        const currentPrice = signal.price;

        if (currentPrice > activeTrade.highestPrice) {
          activeTrade.highestPrice = currentPrice;
        }
      }

      // =========================
      // SELL EXIT
      // =========================
      if (signal.type === "SELL" && activeTrade) {
        const exitPrice = signal.price;

        const pnl =
          ((exitPrice - activeTrade.entryPrice) / activeTrade.entryPrice) * 100;

        const maxProfit =
          ((activeTrade.highestPrice - activeTrade.entryPrice) /
            activeTrade.entryPrice) *
          100;

        trades.push({
          ...activeTrade,

          exitDate: signal.date,
          exitPrice,

          pnl: pnl.toFixed(2),
          maxProfit: maxProfit.toFixed(2),

          status: "CLOSED",
        });

        activeTrade = null;
      }
    }

    return trades;
  };
  const indicators = technicalSnapshot?.indicator_data;
  const masterTimelineDay = technicalSnapshot?.close_series || [];
  const histData = technicalSnapshot;
  const intradayData = marketSnapshot?.intraday;
  const indicatorData = technicalSnapshot?.indicator_data;
  const indicatorsMin = intradayData?.indicators;
  const masterTimelineMin = intradayData?.closeSeries || [];

  //previpus working code
  // const macdChartDataMin = masterTimelineMin.map((p, i) => {
  //   const offset = masterTimelineMin.length - indicatorsMin.macd.length;
  //   const macdIndex = i - offset;

  //   if (macdIndex < 0) {
  //     return {
  //       date: p.date,
  //       MACD: null,
  //       signal: null,
  //       histogram: null,
  //     };
  //   }

  //   const m = indicatorsMin.macd[macdIndex];

  //   return {
  //     date: p.date,
  //     MACD: m.macd,
  //     signal: m.signal,
  //     histogram: m.histogram,
  //   };
  // });
  const macdChartDataMin = useMemo(() => {
    if (!masterTimelineMin?.length || !indicatorsMin?.macd?.length) return [];

    const offset = masterTimelineMin.length - indicatorsMin.macd.length;

    return masterTimelineMin.map((p, i) => {
      const macdIndex = i - offset;

      if (macdIndex < 0) {
        return { date: p.date, MACD: null, signal: null, histogram: null };
      }

      const m = indicatorsMin.macd[macdIndex];
      return {
        date: p.date,
        MACD: m.macd,
        signal: m.signal,
        histogram: m.histogram,
      };
    });
  }, [masterTimelineMin, indicatorsMin?.macd]);
  const minPriceBase = intradayData?.closeSeries || [];
  const dayPriceBase = indicatorData?.closeSeries || [];
  const macdChartData = useMemo(() => {
    const macdArr = technicalSnapshot?.indicator_data?.macd || [];
    const priceArr = technicalSnapshot?.close_series || [];

    return priceArr.map((p) => {
      const m = macdArr.find((item) => item.date === p.date);

      return {
        date: p.date,
        MACD: m?.macd ?? null,
        signal: m?.signal ?? null,
        histogram: m?.histogram ?? null,
      };
    });
  }, [technicalSnapshot]);

  const newsData = useMemo(() => {
    return fundamentals?.news;
  }, [fundamentals]);
  useEffect(() => {
    if (!macdChartDataMin?.length || !minPriceBase?.length) return;

    const signals = detectCrossovers(macdChartDataMin, minPriceBase);

    // setCrossovers((prev) => {
    //   if (JSON.stringify(prev) === JSON.stringify(signals)) {
    //     return prev;
    //   }

    //   return signals;
    // });
    setCrossovers((prev) => {
      if (
        prev.length === signals.length &&
        prev[prev.length - 1]?.date === signals[signals.length - 1]?.date
      ) {
        return prev;
      }
      return signals;
    });
  }, [macdChartDataMin, minPriceBase]);

  //min dropdown for chart
  const resampleMinuteMACD = (data, step) => {
    if (!Array.isArray(data) || step === 1) return data;

    const result = [];

    for (let i = 0; i < data.length; i += step) {
      const chunk = data.slice(i, i + step);
      if (chunk.length < step) continue;

      const last = chunk[chunk.length - 1];

      result.push({
        date: last.date, // last candle time
        MACD: last.MACD,
        signal: last.signal,
        histogram: chunk.reduce((sum, c) => sum + (c.histogram ?? 0), 0),
      });
    }

    return result;
  };

  const resampleMinutePrice = (data, step) => {
    if (!Array.isArray(data) || step === 1) return data;

    const result = [];
    for (let i = 0; i < data.length; i += step) {
      const chunk = data.slice(i, i + step);
      if (chunk.length < step) continue;

      const last = chunk[chunk.length - 1];
      result.push({
        date: last.date,
        close: last.close,
      });
    }
    return result;
  };

  const resampleDailyPrice = (data, interval) => {
    if (!Array.isArray(data) || interval === "1d") return data;

    const groups = new Map();

    data.forEach((row) => {
      const d = new Date(row.date);
      if (isNaN(d.getTime())) return;

      let key;

      if (interval === "1w") {
        const year = d.getFullYear();
        const week = Math.ceil(
          ((d - new Date(year, 0, 1)) / 86400000 +
            new Date(year, 0, 1).getDay() +
            1) /
            7,
        );
        key = `${year}-W${week}`;
      }

      if (interval === "1mo") {
        key = `${d.getFullYear()}-${d.getMonth() + 1}`;
      }

      if (interval === "3mo") {
        const q = Math.floor(d.getMonth() / 3) + 1;
        key = `${d.getFullYear()}-Q${q}`;
      }

      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });

    return Array.from(groups.values()).map((group) => {
      const last = group[group.length - 1];
      return {
        date: last.date,
        close: last.close,
      };
    });
  };
  const selectedPriceData = useMemo(() => {
    if (timeFrame === "min") {
      const stepMap = { "1m": 1, "3m": 3, "5m": 5 };
      return resampleMinutePrice(minPriceBase, stepMap[interval] || 1);
    }

    return resampleDailyPrice(dayPriceBase, interval);
  }, [timeFrame, interval, minPriceBase, dayPriceBase]);

  //day dropdown
  const resampleDailyMACD = (data, interval) => {
    if (!Array.isArray(data) || interval === "1d") return data;

    const groups = new Map();

    data.forEach((row) => {
      const d = new Date(row.date);
      if (isNaN(d.getTime())) return;

      let key;

      // 📅 WEEKLY
      if (interval === "1w") {
        const year = d.getFullYear();
        const week = Math.ceil(
          ((d - new Date(year, 0, 1)) / 86400000 +
            new Date(year, 0, 1).getDay() +
            1) /
            7,
        );
        key = `${year}-W${week}`;
      }

      // 📅 MONTHLY
      if (interval === "1mo") {
        key = `${d.getFullYear()}-${d.getMonth() + 1}`;
      }

      // 📅 3 MONTHS
      if (interval === "3mo") {
        const quarter = Math.floor(d.getMonth() / 3) + 1;
        key = `${d.getFullYear()}-Q${quarter}`;
      }

      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });

    return Array.from(groups.values()).map((group) => {
      const last = group[group.length - 1];

      return {
        date: last.date, // last trading day
        MACD: last.MACD,
        signal: last.signal,
        histogram: group.reduce((sum, g) => sum + (g.histogram ?? 0), 0),
      };
    });
  };
  //chart
  const selectedData = (() => {
    // ⏱ INTRADAY
    if (timeFrame === "min") {
      if (!Array.isArray(macdChartDataMin)) return [];

      if (interval === "1m") return macdChartDataMin;
      if (interval === "3m") return resampleMinuteMACD(macdChartDataMin, 3);
      if (interval === "5m") return resampleMinuteMACD(macdChartDataMin, 5);

      return macdChartDataMin; // safe fallback
    }

    // 📅 DAILY / HIGHER
    if (!Array.isArray(macdChartData)) return [];

    if (interval === "1d") return macdChartData;
    if (interval === "1w") return resampleDailyMACD(macdChartData, "1w");
    if (interval === "1mo") return resampleDailyMACD(macdChartData, "1mo");
    if (interval === "3mo") return resampleDailyMACD(macdChartData, "3mo");

    return macdChartData; // safe fallback
  })();
  const intradayVwap = intradayData;

  //error handling
  // const isFetchingLiveData =
  //   marketLoading || !marketSnapshot || !technicalSnapshot || !quote;
  // const isFetchingLiveData =
  //   !liveReady ||
  //   marketLoading ||
  //   !marketSnapshot ||
  //   !technicalSnapshot ||
  //   !quote;
  const isFetchingLiveData = !liveReady || stockDetailLoading;
  const returnData = technicalSnapshot?.return_data?.returns;
  //return data
  const oneWeekReturn = returnData?.oneWeek;

  const oneMonthReturn = returnData?.oneMonth;
  const ytdReturn = returnData?.ytd;
  const oneYearReturn = returnData?.oneYear;

  const threeYearReturn = returnData?.threeYear;
  const fiveYearReturn = returnData?.fiveYear;
  const getBgColor = (text) => {
    if (text === "BUY" || text === "Bullish") {
      return "#249966";
    } else if (text === "SELL" || text === "Bearish") {
      return "#dc2626";
    } else {
      return "#fbbf24";
    }
  };
  const day_pred = technicalSnapshot?.prediction;
  const min_pred = rsi_macd_signal;
  const day_result = technicalSnapshot?.prediction_result;
  const min_result = intraday?.prediction_result;
  const chartIndicatorData = intraday?.chart_indicator;
  const formatTimestamp = (dateValue) => {
    if (!dateValue) return "";

    const now = new Date(dateValue);
    if (isNaN(now)) return "";

    const day = String(now.getDate()).padStart(2, "0");

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const month = monthNames[now.getMonth()];
    const year = now.getFullYear();

    const time = now.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    return `${day}-${month}-${year} | ${time}`;
  };

  const formattedQuoteTime = formatTimestamp(marketSnapshot?.quote_updated_at);
  const formattedIntradayTime = formatTimestamp(
    marketSnapshot?.intraday_updated_at,
  );

  const formatVolume = (volume) => {
    if (volume == null) return "-"; // catches null & undefined

    const num = Number(volume);
    if (!Number.isFinite(num)) return "-";

    return num.toLocaleString("en-IN");
  };
  const handleRefresh = async () => {
    try {
      if (!symbol) return;

      await getStockDetailSnapshot(symbol, paramSecurityId);
    } catch (error) {
      console.error("Stock detail refresh failed", error);
    }
  };

  const statisticsData = useMemo(() => {
    return fundamentals?.keyStatistics?.body;
  }, [fundamentals]);
  // const peRatio = (useMemo) => (
  //   () => {
  //     return statisticsData?.forwardPE.fmt;
  //   },
  //   [statisticsData]
  // );
  const peRatio = useMemo(
    () => statisticsData?.forwardPE?.fmt ?? "—",
    [statisticsData],
  );

  //donwload report btn

  const safeNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(num) ? num : null;
  };

  const buildPDFPayload = () => {
    const quote = marketSnapshot?.quote?.data;
    const ohlc = quote?.ohlc;
    const intraday = marketSnapshot?.intraday;
    const indicatorData = technicalSnapshot?.indicator_data;
    const returns = technicalSnapshot?.return_data?.returns;

    const latestRSI =
      intraday?.indicators?.rsi?.length > 0
        ? intraday.indicators.rsi.at(-1)?.value
        : indicatorData?.indicators?.rsi?.at?.(-1)?.value;

    const latestMACD =
      intraday?.indicators?.macd?.length > 0
        ? intraday.indicators.macd.at(-1)?.macd
        : indicatorData?.indicators?.macd?.at?.(-1)?.macd;

    const targetPriceData = fundamentals?.targetPrice;
    const keyStats = fundamentals?.keyStatistics?.body;

    return {
      overview: {
        symbol,
        companyName: stockName?.company || stockName?.name || symbol,
        ltp: quote?.last_price,
        open: ohlc?.open,
        high: ohlc?.high,
        low: ohlc?.low,
        previousClose: ohlc?.close,
        percentChange: Number(percent_change),
        dayHigh: ohlc?.high,
        dayLow: ohlc?.low,
        week52High: quote?.["52_week_high"],
        week52Low: quote?.["52_week_low"],
        volume: quote?.volume,
      },

      forecast: {
        targetPrice: safeNumber(targetPriceData?.target_price),
        targetLow: safeNumber(targetPriceData?.target_low),
        targetHigh: safeNumber(targetPriceData?.target_high),
        upsidePercent: Number(projectChange || 0),
      },

      technicals: {
        rsi: latestRSI,
        macd: latestMACD,
        vwap: intraday?.vwap,
        vwapSignal: intraday?.vwap_signal,
        utSignal: intraday?.ut_signal,
        bbaweSignal: intraday?.bbawe_signal,
        rsiMacdSignal: intraday?.prediction,
        nearestResistance: nearestResistance?.value,
        nearestSupport: nearestSupport?.value,
        stopLoss: analysis?.stopLoss,
        traderNote: analysis?.traderNote,
        signal: analysis?.signal,
        exit: analysis?.exit,
      },

      fundamentals: {
        peRatio: keyStats?.forwardPE?.fmt || keyStats?.trailingPE?.fmt,
        marketCap: keyStats?.marketCap?.fmt,
        beta: keyStats?.beta?.fmt,
        bookValue: keyStats?.bookValue?.fmt,
        profitMargins: keyStats?.profitMargins?.fmt,
        returnOnEquity: keyStats?.returnOnEquity?.fmt,
        debtToEquity: keyStats?.debtToEquity?.fmt,
      },

      returns: {
        oneWeek: safeNumber(returns?.oneWeek),
        oneMonth: safeNumber(returns?.oneMonth),
        ytd: safeNumber(returns?.ytd),
        oneYear: safeNumber(returns?.oneYear),
        threeYear: safeNumber(returns?.threeYear),
        fiveYear: safeNumber(returns?.fiveYear),
      },

      marketDepth: {
        buyPercent,
        sellPercent,
        totalBuyQty,
        totalSellQty,
        buyOrders,
        sellOrders,
      },

      pivots: {
        timeframe: resistTimeFrame,
        classic: resistanceDataToRender?.classic,
        fibonacci: resistanceDataToRender?.fibonacci,
        camarilla: resistanceDataToRender?.camarilla,
        woodie: resistanceDataToRender?.woodie,
        demark: resistanceDataToRender?.demark,
      },

      movingAverages: {
        rows,
        summary,
      },

      meta: {
        generatedAt: new Date().toISOString(),
        timeframe: timeFrame,
        quoteUpdatedAt: marketSnapshot?.quote_updated_at,
        intradayUpdatedAt: marketSnapshot?.intraday_updated_at,
        source: "StockDetailPage",
      },
    };
  };

  const handleDownloadReport = async () => {
    try {
      if (!stockDetail || !marketSnapshot || !quote) {
        window.alert("Stock data not ready. Please refresh once.");
        return;
      }

      await downloadStockPDF(buildPDFPayload(), symbol);
      window.alert("✅ Report downloaded successfully!");
    } catch (err) {
      console.error("PDF generation failed", err);
      window.alert("❌ PDF generation failed");
    }
  };

  const intradaySmartMoney =
    marketSnapshot?.smart_money || marketSnapshot?.intraday?.smart_money;

  const eodSmartMoney =
    technicalSnapshot?.smart_money ||
    technicalSnapshot?.indicator_data?.smart_money;

  const activeSmartMoney =
    smartMode === "intraday" ? intradaySmartMoney : eodSmartMoney;
  const getSmartMoneyColor = (status) => {
    if (status === "Strong Accumulation" || status === "Accumulation") {
      return "#249966"; // same green as Bullish
    } else if (status === "Distribution" || status === "Heavy Distribution") {
      return "#dc2626"; // same red as Bearish
    } else {
      return "#fbbf24"; // same yellow as Neutral
    }
  };
  return (
    <>
      <div className="last-updated-head">
        {formattedQuoteTime && <span>As on {formattedQuoteTime}</span>}
        <FiRefreshCw
          onClick={handleRefresh}
          size={18}
          style={{
            cursor: "pointer",
            transition: "0.2s",
          }}
          onMouseOver={(e) => (e.target.style.color = "#2fd18a")}
          onMouseOut={(e) => (e.target.style.color = "#9ca3af")}
        />
      </div>

      {isFetchingLiveData && !stockDetailError && (
        <p
          style={{
            fontSize: "1rem",
            fontWeight: "700",
            display: "flex",
            gap: "1rem",
          }}
        >
          Fetching Live data...
          <span style={{ paddingLeft: "10px", marginTop: "5px" }}>
            <FaSpinner />
          </span>
        </p>
      )}

      {stockDetailError && (
        <div
          style={{
            marginTop: "20px",
            padding: "14px",
            borderLeft: "5px solid #dc2626",
            background: "#fee2e2",
            color: "#7f1d1d",
            fontWeight: 600,
            borderRadius: "8px",
          }}
        >
          {stockDetailError}
        </div>
      )}

      {/* {liveReady && quote && ( */}

      <>
        {!isFetchingLiveData && (
          <div className={`page-container ${chatOpen ? "shrink" : ""}`}>
            <div className="page-inner-container">
              <div className="stock-detail-card">
                <div className="analysis-container">
                  <div className="analysis-header">
                    <div className="analysis-head-name">
                      <div className="analysis-head-div">
                        <h2 className="stock-title">{stockName?.company}</h2>
                        <span className="stock-tag">{symbol}</span>
                      </div>

                      <div className="price-chip-head">
                        <div className="price-chip">
                          <p className="label">
                            LTP <InfoTooltip text={TOOLTIPS?.ltp} />
                          </p>
                          <h3 className="value" style={{ color: "#2563eb" }}>
                            ₹{last_price}
                          </h3>
                        </div>

                        <div className="price-chip">
                          <p className="label">
                            % Change
                            <InfoTooltip text={TOOLTIPS?.percentChange} />
                          </p>
                          <h3
                            className="value positive"
                            style={{
                              color: percent_change > 0 ? "#249966" : "#f87171",
                            }}
                          >
                            {percent_change}%
                          </h3>
                        </div>
                        <div className="price-chip">
                          <p className="label">
                            Open
                            <InfoTooltip text={TOOLTIPS?.open} />
                          </p>
                          <h3 className="value">₹{open}</h3>
                        </div>
                        <div className="price-chip">
                          <p className="label">
                            Close
                            <InfoTooltip text={TOOLTIPS?.close} />
                          </p>
                          <h3 className="value">₹{close}</h3>
                        </div>

                        <div className="price-chip">
                          <p className="label">
                            Forecast Price
                            <InfoTooltip text={TOOLTIPS?.forecastPrice} />
                          </p>
                          <h3
                            className="value"
                            style={{
                              color:
                                Number.isFinite(target_price) &&
                                target_price > quote?.last_price
                                  ? "#249966"
                                  : "#f87171",
                            }}
                          >
                            {target_price && Number.isFinite(target_price)
                              ? `₹${target_price}`
                              : "—"}
                          </h3>
                        </div>
                        <div className="price-chip">
                          <p className="label">
                            VWAP <InfoTooltip text={TOOLTIPS?.vwap} />
                          </p>
                          <h3 className="value">₹{vwap?.toFixed(2) || "--"}</h3>
                        </div>
                      </div>
                    </div>

                    <div className="action-buttons">
                      <button
                        className="rec-btn"
                        onClick={handleDownloadReport}
                      >
                        Download Report
                      </button>

                      <span
                        className={`watchlist-icon ${isActive ? "active" : "inactive"}`}
                        // onClick={() => handleAdd(companyCode)}
                      >
                        {isActive ? (
                          <FaStar className="star" />
                        ) : (
                          <FiStar className="star" />
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="outer-insight-box">
                    <div
                      className="insight-box"
                      // style={{
                      //   borderLeft:
                      //     timeFrame === "min"
                      //       ? `4px solid ${color1}`
                      //       : `4px solid ${color}`,
                      // }}
                    >
                      <div className="insight-head-div">
                        <p className="insight-head">Quant Kubera Insights</p>
                      </div>

                      <div className="vwap-rsi-signal">
                        <div className="sub-insight vwap-card">
                          <div className="vwap-header">
                            <div
                              className="vwap-sub-header"
                              style={{ marginBottom: "12px" }}
                            >
                              <span>
                                RSI & MACD BASED SIGNAL{" "}
                                <InfoTooltip text={TOOLTIPS?.rsiMacdSignal} />
                              </span>
                              <select
                                name=""
                                id=""
                                value={timeFrame}
                                onChange={(e) => setTimeFrame(e.target.value)}
                                style={{
                                  background: "inherit",

                                  fontWeight: "600",
                                  color: "black",

                                  boxShadow: " 0 4px 12px rgba(0, 0, 0, 0.4)",
                                  padding: "5px 7px",
                                  borderRadius: "10px",
                                }}
                              >
                                <option value="min">Minute</option>
                                <option value="day">Day</option>
                              </select>
                            </div>
                          </div>

                          <div className="insight-sub-div">
                            <div
                              className="insight-text"
                              // style={{
                              //   color: timeFrame === "min" ? color1 : color,
                              // }}
                            >
                              {timeFrame === "min" ? min_result : day_result}
                              {/* {intraday?.prediction_result} */}
                            </div>
                            <button
                              className="insight-tag rec-btn"
                              style={{
                                backgroundColor:
                                  timeFrame === "min"
                                    ? getBgColor(min_pred)
                                    : getBgColor(day_pred),
                                color: "white",
                              }}
                            >
                              {/* {rsi_macd_signal} */}
                              {timeFrame === "min" ? min_pred : day_pred}
                            </button>
                          </div>
                        </div>

                        <div className="vwap-card">
                          <div className="vwap-header">
                            <div className="vwap-sub-header">
                              <span className="vwap-sub-header-span">
                                VWAP MOMENTUM SIGNAL{" "}
                                <InfoTooltip text={TOOLTIPS?.vwapMomentum} />
                              </span>
                              <button
                                className={` insight-tag rec-btn
                              vwap-badge ${
                                vwap_signal === "Bullish"
                                  ? "green"
                                  : vwap_signal === "Bearish"
                                    ? "red"
                                    : "grey"
                              }`}
                              >
                                {vwap_signal}
                              </button>
                              {/* <span className="intraday-badge">Intraday</span> */}
                            </div>
                            <div className="insight-sub-div">
                              <span
                                style={{
                                  alignSelf: "center",
                                }}
                              >
                                LTP - VWAP:
                                <span
                                  style={{
                                    color: diff > 0 ? "#16a34a" : "#dc2626",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {diff > 0 ? " +" : ""}
                                  {diff.toFixed(2)} (
                                  {percent > 0 ? `+${percent}` : `${percent}`}
                                  %)
                                </span>
                              </span>

                              {/* <button
                                className={` insight-tag
                              vwap-badge ${
                                vwap_signal === "Bullish"
                                  ? "green"
                                  : vwap_signal === "Bearish"
                                    ? "red"
                                    : "grey"
                              }`}
                              >
                                {vwap_signal}
                              </button> */}
                            </div>
                          </div>

                          <p className="vwap-text">
                            {vwap_signal === "Bullish" &&
                              `Price is above VWAP (₹${vwap?.toFixed(
                                2,
                              )}) — bullish intraday strength.`}

                            {vwap_signal === "Bearish" &&
                              `Price is below VWAP (₹${vwap?.toFixed(
                                2,
                              )}) — bearish pressure likely.`}

                            {vwap_signal === "Neutral" &&
                              `Price is near VWAP (₹${vwap?.toFixed(
                                2,
                              )}) — wait for breakout.`}
                          </p>
                        </div>
                        <div className="vwap-card ut-bb-card">
                          <div className="vwap-header">
                            <div className="sub-vwap-head ut-bb-subhead">
                              <div className="vwap-sub-header">
                                <span>
                                  UT BOT SIGNAL{" "}
                                  <InfoTooltip text={TOOLTIPS?.utBot} />
                                </span>
                              </div>
                              <button
                                className={` insight-tag rec-btn
                              vwap-badge ${
                                ut_signal === "Bullish" ? "green" : "red"
                              }`}
                              >
                                {ut_signal}
                                {/* {`${ut_signal === "BUY" ? "Bullish" : "Bearish"}`} */}
                              </button>
                            </div>
                          </div>
                          <div className="vwap-header">
                            <div className="sub-vwap-head ut-bb-subhead">
                              <div className="vwap-sub-header">
                                <span>
                                  BB SIGNAL{" "}
                                  <InfoTooltip text={TOOLTIPS?.bbSignal} />
                                </span>
                              </div>
                              <button
                                className={` insight-tag rec-btn
                              vwap-badge ${
                                bb_signal === "BUY" ? "green" : "red"
                              }`}
                              >
                                {bb_signal === "BUY" ? "Bullish" : "Bearish"}
                              </button>
                            </div>
                          </div>
                        </div>
                        {/* <div className="vwap-card">
                          <div className="vwap-header">
                            <div className="sub-vwap-head">
                              <div className="vwap-sub-header">
                                <span>
                                  BB SIGNAL{" "}
                                  <InfoTooltip text={TOOLTIPS?.bbSignal} />
                                </span>
                              </div>
                              <button
                                className={` insight-tag
                              vwap-badge ${
                                bb_signal === "BUY" ? "green" : "red"
                              }`}
                              >
                                {bb_signal === "BUY" ? "Bullish" : "Bearish"}
                              </button>
                            </div>
                          </div>
                        </div> */}
                        <div className="vwap-card smart-money-card">
                          <div className="vwap-header">
                            <div className="vwap-sub-header">
                              <span>
                                SMART MONEY FLOW{" "}
                                <InfoTooltip text={TOOLTIPS?.smartMoney} />
                              </span>

                              <button
                                className="rec-btn"
                                style={{
                                  backgroundColor: getBgColor(
                                    activeSmartMoney?.signal,
                                  ),
                                }}
                                // style={{
                                //   backgroundColor: getBgColor(
                                //     activeSmartMoney?.status,
                                //   ),
                                // }}
                              >
                                {activeSmartMoney?.signal || "Refreshing"}
                                {/* <InfoTooltip
                                  text={TOOLTIPS?.smartMoneyStatus}
                                /> */}
                              </button>

                              <select
                                value={smartMode}
                                onChange={(e) => setSmartMode(e.target.value)}
                                style={{
                                  background: "inherit",
                                  fontWeight: "600",
                                  color: "black",
                                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                                  padding: "5px 7px",
                                  borderRadius: "10px",
                                }}
                              >
                                <option value="intraday">Intraday</option>
                                <option value="eod">EOD</option>
                              </select>
                            </div>

                            <div className="smart-money-content">
                              <div className="sub-money-content">
                                <p>
                                  Score:{" "}
                                  <strong>
                                    {activeSmartMoney?.score ?? "-"}/100
                                  </strong>
                                  <InfoTooltip
                                    text={TOOLTIPS?.smartMoneyScore}
                                  />
                                </p>

                                <p>
                                  CMF:{" "}
                                  <strong>
                                    {activeSmartMoney?.cmf?.value ?? "-"}
                                  </strong>
                                  <InfoTooltip text={TOOLTIPS?.cmf} />
                                </p>

                                <p>
                                  RVOL:{" "}
                                  <strong>
                                    {activeSmartMoney?.rvol?.value
                                      ? `${activeSmartMoney.rvol.value}x`
                                      : "-"}
                                  </strong>
                                  <InfoTooltip text={TOOLTIPS?.rvol} />
                                </p>
                              </div>
                            </div>

                            <p className="smart-money-conclusion">
                              {activeSmartMoney?.conclusion ||
                                "Smart money data refreshing."}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>{" "}
                    {/* Pivot signals */}
                    <div className="tp-card  tp-insight">
                      <h4 className="tp-title insight-critical-title">
                        <span className="tp-insight-span">
                          Near Pivots ⭐ ⭐ ⭐{" "}
                          <InfoTooltip text={TOOLTIPS?.pivotLevels} />
                          <span style={{ color: "#2563eb" }}>
                            LTP - ₹{quote?.last_price}
                          </span>
                        </span>
                      </h4>

                      <div className="near-pivot-grid">
                        <div className="np-card np-pivot-row">
                          <span className="np-label">Key Resistance</span>
                          <span className="np-value red">
                            {nearestResistance?.value
                              ? `₹${nearestResistance.value.toFixed(2)}`
                              : "All Broken"}
                          </span>
                        </div>

                        <div className="np-card">
                          <span className="np-label">Key Support</span>
                          <span className="np-value green">
                            ₹{nearestSupport?.value?.toFixed(2)}
                          </span>
                        </div>

                        <div className="np-card">
                          <span className="np-label">VWAP</span>
                          <span className="np-value blue">
                            ₹{latestVWAP?.toFixed(2)}
                          </span>
                        </div>

                        <div className="np-card">
                          <span className="np-label">Stop Loss</span>
                          <span className="np-value gray">
                            ₹{analysis?.stopLoss}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="factor-box">
                    <div className="vwap-entry-exit">
                      <div className="factor-box vwap-factor-box">
                        {/* <h4>Vwap Entry Exit Stop Loss</h4> */}
                        <h4>
                          Vwap Key Levels
                          <InfoTooltip text={TOOLTIPS?.vwapKeyLevels} />
                        </h4>
                        <div className="tp-row-container">
                          <div className="tp-card tp-entry">
                            {/* <h4 className="tp-title">ENTRY ZONE</h4> */}
                            <h4 className="tp-title">
                              VWAP READOUT
                              <InfoTooltip text={TOOLTIPS?.vwapReadout} />
                            </h4>

                            <div className="tp-entry-content">
                              <div className="tp-entry-top">
                                <h2
                                  className={`tp-signal tp-${analysis?.entry?.type}`}
                                >
                                  {analysis?.entry?.label}
                                </h2>
                              </div>
                              <span className="tp-vwap-inline">
                                <span>VWAP</span>₹{latestVWAP?.toFixed(2)}
                              </span>
                              <span className="tp-inline-text">
                                {analysis?.entry?.msg}
                              </span>
                            </div>
                          </div>

                          <div className="tp-card tp-target">
                            {/* <h4 className="tp-title">EXIT TARGETS</h4> */}
                            <h4 className="tp-title">
                              KEY LEVELS
                              <InfoTooltip text={TOOLTIPS?.vwapKeyLevels} />
                            </h4>

                            <div className="tp-target-block">
                              <div className="tp-target-row">
                                <span className="tp-target-label">
                                  {nearestResistance?.key} (Resistance)
                                </span>

                                <span className="tp-target-value">
                                  {nearestResistance?.value
                                    ? `₹${nearestResistance.value.toFixed(2)}`
                                    : "All Broken"}
                                </span>
                              </div>

                              <p className="tp-target-type">(Primary)</p>
                            </div>

                            <div className="tp-target-block">
                              <div className="tp-target-row">
                                <span className="tp-target-label">
                                  {nearestSupport?.key} (Support)
                                </span>

                                <span className="tp-target-value">
                                  ₹{nearestSupport?.value?.toFixed(2)}
                                </span>
                              </div>

                              <p className="tp-target-type">(Aggressive)</p>
                            </div>
                          </div>

                          <div className="tp-card tp-stop">
                            <h4 className="tp-title">
                              STOP LOSS
                              <InfoTooltip text={TOOLTIPS?.stopLoss} />
                            </h4>

                            <div className="tp-stop-content">
                              <h2 className="tp-stop-value">
                                ₹{analysis?.stopLoss}
                              </h2>
                              <p className="tp-subtext">Based on VWAP</p>
                            </div>
                          </div>
                          <div className="tp-card tp-critical-card">
                            <h4 className="tp-title">
                              SUMMARY
                              <InfoTooltip text={TOOLTIPS?.summary} />
                            </h4>

                            <div className="tp-summary-box">
                              {analysis?.signal && (
                                <p className="tp-signal-text buy">
                                  {analysis.signal}
                                  <InfoTooltip
                                    text={getSignalTooltip(analysis?.signal)}
                                  />
                                </p>
                              )}

                              {analysis?.exit && (
                                <p className="tp-signal-text sell">
                                  {analysis.exit}
                                </p>
                              )}

                              <span className="tp-vwap-inline">
                                <span>{analysis?.pythonTrend}</span>
                              </span>
                              <span className="tp-inline-text">
                                {analysis?.traderNote}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="max-option">
                        <div
                          className="option-chain-div
                "
                        >
                          <p>Option Chain</p>
                          <div className="option-chain-inner-div">
                            <button
                              className="rec-btn"
                              onClick={() => {
                                navigate(`/optionpage/${symbol}`);
                              }}
                            >
                              View
                            </button>
                          </div>
                        </div>
                        <div
                          className="option-chain-div
                "
                        >
                          <p>Max Pain</p>
                          <div className="option-chain-inner-div">
                            <button
                              className="rec-btn"
                              onClick={() => {
                                navigate(`/maxpainpage/${Symbol}`);
                              }}
                            >
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="factor-box">
                      <h4 className="pivot-level-head">
                        <span className="pivot-tooltip">
                          Pivot Levels{" "}
                          <InfoTooltip text={TOOLTIPS?.pivotLevels} />
                        </span>

                        <span
                          style={{
                            display: "flex",
                            justifyContent: "end",
                            // margin: "10px 0",
                          }}
                        >
                          <div className="chart-select">
                            <label>Interval</label>
                            <select
                              name=""
                              id=""
                              value={resistTimeFrame}
                              onChange={(e) =>
                                setResistTimeFrame(e.target.value)
                              }
                              style={{
                                background: "inherit",

                                fontWeight: "600",
                                color: "black",

                                boxShadow: " 0 4px 12px rgba(0, 0, 0, 0.4)",
                                padding: "5px 7px",
                                borderRadius: "10px",
                              }}
                            >
                              <option value="week">Weekly</option>
                              <option value="day">Day</option>
                            </select>
                          </div>
                        </span>
                      </h4>

                      <div className="pivot-container">
                        <table className="pivot-table ma-table">
                          <thead>
                            <tr>
                              <th>Pivot</th>
                              <th>Priority</th>
                              <th>Status</th>
                              <th>Classic</th>
                              <th>Fibonacci</th>
                              <th>Camarilla</th>
                              <th>Woodie</th>
                              <th>DM</th>
                            </tr>
                          </thead>

                          <tbody>
                            {levels?.map((level) => {
                              const classicValue =
                                resistanceDataToRender?.classic?.[level?.key];

                              const status = getStatus(classicValue, ltp);
                              const priority = getPriority(classicValue, ltp);

                              return (
                                <tr key={level?.key}>
                                  <td className="pivot-td-row">
                                    <span>
                                      {level?.key} ({level?.label})
                                    </span>
                                  </td>

                                  <td
                                    className="pivot-td-row"
                                    style={{ textAlign: "center" }}
                                  >
                                    <p className="render-stars">
                                      {renderStars(priority)}
                                    </p>
                                  </td>

                                  <td
                                    className="pivot-td-row"
                                    style={{
                                      fontWeight: "600",
                                      color:
                                        status === "Above LTP"
                                          ? "#dc2626" // red
                                          : status === "Below LTP"
                                            ? "#16a34a" // green
                                            : "#6b7280", // gray
                                    }}
                                  >
                                    <span>{status}</span>
                                  </td>

                                  <td className="pivot-td-row">
                                    <span>
                                      {classicValue?.toFixed(2) ?? "—"}
                                    </span>
                                  </td>

                                  <td className="pivot-td-row">
                                    <span>
                                      {resistanceDataToRender?.fib?.[
                                        level?.key
                                      ]?.toFixed(2) ?? "—"}
                                    </span>
                                  </td>

                                  <td>
                                    <span>
                                      {resistanceDataToRender?.cam?.[
                                        level?.key
                                      ]?.toFixed(2) ?? "—"}
                                    </span>
                                  </td>

                                  <td>
                                    <span>
                                      {resistanceDataToRender?.woodie?.[
                                        level?.key
                                      ]?.toFixed(2) ?? "—"}
                                    </span>
                                  </td>

                                  <td>
                                    <span>
                                      {resistanceDataToRender?.demark?.[
                                        level?.key
                                      ]?.toFixed(2) ?? "—"}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Pice scale forcast day and 52  */}
                  <div className="price-summary">
                    <div className="price-scales">
                      <div className="scale-block">
                        <div className="label">
                          Forecast Range
                          <InfoTooltip text={TOOLTIPS?.forecastRange} />
                        </div>
                        <div className="range-svg-wrapper">
                          <RangeBarSVG
                            title="Forecast Range"
                            low={target_low}
                            high={target_high}
                            ltp={target_price}
                            variant={variant}
                            price={last_price}
                            type="target"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="price-scales">
                      <div className="scale-block">
                        <div className="label">
                          Day Range <InfoTooltip text={TOOLTIPS?.dayRange} />
                        </div>
                        <div className="range-svg-wrapper">
                          <RangeBarSVG
                            title="Day Range"
                            low={low}
                            high={high}
                            ltp={last_price}
                            price={last_price}
                            variant={variant}
                            type="price"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="price-scales">
                      <div className="scale-block">
                        <div className="label">
                          52 Week Range{" "}
                          <InfoTooltip text={TOOLTIPS?.week52Range} />
                        </div>
                        <div className="range-svg-wrapper">
                          <RangeBarSVG
                            title="52 Week Range"
                            low={weekLow}
                            high={weekHigh}
                            ltp={last_price}
                            price={last_price}
                            variant={variant}
                            type="price"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Wealth return data */}
                  <div className="factor-box">
                    <h4>Wealth Creator/Destroyer </h4>
                    <div>
                      <div className="return-box">
                        <div className="return-label">
                          <p className="return-p label">1W</p>
                          <div
                            className={`return-value ${
                              oneWeekReturn > 0
                                ? "return-positive"
                                : "return-negative"
                            }`}
                          >
                            {oneWeekReturn > 0 ? "+" : ""}
                            {oneWeekReturn}%
                          </div>
                        </div>

                        <div className="return-label">
                          <p className="return-p label">1M</p>
                          <div
                            className={`return-value ${
                              oneMonthReturn > 0
                                ? "return-positive"
                                : "return-negative"
                            }`}
                          >
                            {oneMonthReturn > 0 ? "+" : ""}
                            {oneMonthReturn}%
                          </div>
                        </div>
                        <div className="return-label">
                          <p className="return-p label">YTD</p>
                          <div
                            className={`return-value ${
                              ytdReturn > 0
                                ? "return-positive"
                                : "return-negative"
                            }`}
                          >
                            {ytdReturn > 0 ? "+" : ""}
                            {ytdReturn}%
                          </div>
                        </div>
                        <div className="return-label">
                          {" "}
                          <p className="return-p label">1Y</p>
                          <div
                            className={`return-value ${
                              oneYearReturn > 0
                                ? "return-positive"
                                : "return-negative"
                            }`}
                          >
                            {oneYearReturn > 0 ? "+" : ""}
                            {oneYearReturn}%
                          </div>
                        </div>
                        <div className="return-label">
                          {" "}
                          <p className="return-p label">3Y</p>
                          <div
                            className={`return-value ${
                              threeYearReturn > 0
                                ? "return-positive"
                                : "return-negative"
                            }`}
                          >
                            {threeYearReturn > 0 ? "+" : ""}
                            {threeYearReturn}%
                          </div>
                        </div>
                        <div className="return-label">
                          <p className="return-p label">5Y</p>
                          <div
                            className={`return-value ${
                              fiveYearReturn > 0
                                ? "return-positive"
                                : "return-negative"
                            }`}
                          >
                            {fiveYearReturn > 0 ? "+" : ""}
                            {fiveYearReturn}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Technical factors */}
                  <div className="factor-wrapper">
                    <div className="factor-box">
                      <h4>
                        Technical Factors{" "}
                        <InfoTooltip text={TOOLTIPS?.technicalFactors} />{" "}
                      </h4>
                      <div className="inner-factor-box">
                        <div className="factor-row">
                          <p className="label">
                            RSI
                            <InfoTooltip text={TOOLTIPS?.rsi} />
                          </p>
                          <span
                            style={{
                              color: intraday?.rsi > 0 ? "#249966" : "#f87171",
                            }}
                          >
                            {" "}
                            {intraday?.rsi?.toFixed(2)}
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            MACD
                            <InfoTooltip text={TOOLTIPS?.macd} />
                          </p>
                          <span
                            style={{
                              color: intraday?.macd > 0 ? "#249966" : "#f87171",
                            }}
                          >
                            {intraday?.macd?.toFixed(2)}
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            Volume <InfoTooltip text={TOOLTIPS?.volume} />
                          </p>

                          <span> {formatVolume(quote?.volume)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="factor-box">
                      <h4>
                        Fundamental Factors{" "}
                        <InfoTooltip text={TOOLTIPS?.fundamentalFactors} />
                      </h4>
                      <div className="inner-factor-box">
                        <div className="factor-row">
                          <p className="label">
                            % Upside <InfoTooltip text={TOOLTIPS?.upside} />
                          </p>
                          <span
                            className="positive"
                            style={{
                              color: projectChange > 0 ? "#249966" : "#f87171",
                            }}
                          >
                            {" "}
                            {projectChange}%
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            Forecast Price{" "}
                            <InfoTooltip text={TOOLTIPS?.forecastPrice} />
                          </p>
                          <span>₹{target_price}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            P/E Ratio <InfoTooltip text={TOOLTIPS?.peRatio} />
                          </p>
                          <span>{peRatio}</span>
                        </div>
                        {/* <div className="factor-row">
                          <p className="label">Face Value</p>
                          <span>₹{marketCapData?.faceValue}</span>
                        </div> */}
                      </div>
                    </div>
                  </div>

                  {/* Vlaution mesure */}
                  <div className="val-1">
                    <div className="factor-box">
                      <h4>
                        Valuation Measures{" "}
                        <InfoTooltip text={TOOLTIPS?.valuationMeasures} />
                      </h4>
                      <div className="inner-factor-box">
                        {/* <div className="factor-row">
                          <p className="label">Market Cap</p>
                          <span>
                            {" "}
                            {formatMarketCapINR(marketCapData?.marketCap)}
                          </span>
                        </div> */}
                        <div className="factor-row">
                          <p className="label">
                            Enterprise Value{" "}
                            <InfoTooltip text={TOOLTIPS?.enterpriseValue} />
                          </p>
                          <span> {statisticsData?.enterpriseValue?.fmt}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            Forward P/E{" "}
                            <InfoTooltip text={TOOLTIPS?.forwardPE} />
                          </p>
                          <span>{statisticsData?.forwardPE?.fmt}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            Price to Book{" "}
                            <InfoTooltip text={TOOLTIPS?.priceToBook} />
                          </p>
                          <span>{statisticsData?.priceToBook?.fmt}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            Enterprise/Revenue{" "}
                            <InfoTooltip text={TOOLTIPS?.enterpriseToRevenue} />
                          </p>
                          <span>
                            {statisticsData?.enterpriseToRevenue?.fmt}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Profitablitu */}
                  <div className="factor-wrapper">
                    <div className="factor-box">
                      <h4>
                        Profitability & Growth{" "}
                        <InfoTooltip text={TOOLTIPS?.profitability} />
                      </h4>
                      <div className="inner-factor-box">
                        <div className="factor-row">
                          <p className="label">
                            Profit Margin{" "}
                            <InfoTooltip text={TOOLTIPS?.profitMargin} />
                          </p>
                          <span>{statisticsData?.profitMargins?.fmt}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            Earnings Growth (QoQ){" "}
                            <InfoTooltip text={TOOLTIPS?.earningsGrowth} />
                          </p>
                          <span>
                            {statisticsData?.earningsQuarterlyGrowth?.fmt}
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            Trailing EPS{" "}
                            <InfoTooltip text={TOOLTIPS?.trailingEps} />
                          </p>
                          <span>{statisticsData?.trailingEps?.fmt}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            Forward EPS{" "}
                            <InfoTooltip text={TOOLTIPS?.forwardEps} />
                          </p>
                          <span>{statisticsData?.forwardEps?.fmt}</span>
                        </div>
                      </div>
                    </div>
                    <div className="factor-box">
                      <h4>
                        Ownership & Risk{" "}
                        <InfoTooltip text={TOOLTIPS?.ownershipRisk} />
                      </h4>
                      <div className="inner-factor-box">
                        <div className="factor-row">
                          <p className="label">Insider Holding %</p>
                          <span>
                            {statisticsData?.heldPercentInsiders?.fmt}
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            Institutional Holding %{" "}
                            <InfoTooltip
                              text={TOOLTIPS?.institutionalHolding}
                            />
                          </p>
                          <span>
                            {statisticsData?.heldPercentInstitutions?.fmt}
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            Float Shares{" "}
                            <InfoTooltip text={TOOLTIPS?.floatShares} />
                          </p>
                          <span>{statisticsData?.floatShares?.fmt}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">
                            Shares Outstanding{" "}
                            <InfoTooltip text={TOOLTIPS?.sharesOutstanding} />
                          </p>
                          <span>{statisticsData?.sharesOutstanding?.fmt}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ma data */}
                  <div className="factor-box ma-factor-box">
                    <div className="ma-panel">
                      <h4>
                        Moving Averages{" "}
                        <InfoTooltip text={TOOLTIPS?.movingAverage} />
                      </h4>
                      <div className="ma-div">
                        <Gauge
                          buy={summary?.finalBuy}
                          sell={summary?.finalSell}
                        />

                        <table className="ma-table pivot-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Simple</th>
                              <th></th>
                              <th>Exponential</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows?.length > 0 ? (
                              rows?.map((row) => (
                                <tr key={row?.name}>
                                  <td>
                                    <span>{row?.name}</span>
                                  </td>

                                  <td>
                                    <span className="ma-value">
                                      {row?.smaValue.toFixed(2)}
                                    </span>
                                  </td>
                                  <td>
                                    <span
                                      className={
                                        row.smaTrend === "Buy"
                                          ? "ma-trend-buy"
                                          : "ma-trend-sell"
                                      }
                                    >
                                      {row?.smaTrend}
                                    </span>
                                  </td>

                                  <td>
                                    <span className="ma-value">
                                      {row?.emaValue?.toFixed(2)}
                                    </span>
                                  </td>
                                  <td>
                                    <span
                                      className={
                                        row?.emaTrend === "Buy"
                                          ? "ma-trend-buy"
                                          : "ma-trend-sell"
                                      }
                                    >
                                      {row?.emaTrend}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <div className="single-stock-table-row">
                                <span>Loading MA Data...</span>
                              </div>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Order book depth in quote */}
                  <div className="factor-box">
                    <h4>
                      Order Book <InfoTooltip text={TOOLTIPS?.orderBook} />
                    </h4>
                    <table className="orderbook-table">
                      <thead>
                        <tr>
                          <th>Qty</th>
                          <th>Bid (₹)</th>
                          <th>Ask (₹)</th>
                          <th>Qty</th>
                        </tr>
                      </thead>

                      <tbody>
                        {Array.from({
                          length: Math.max(
                            depth?.buy?.length || 0,
                            depth?.sell?.length || 0,
                          ),
                        }).map((_, idx) => {
                          const buy = depth?.buy?.[idx];
                          const sell = depth?.sell?.[idx];

                          return (
                            <tr key={idx}>
                              <td className="buy-qty">
                                {buy ? buy?.quantity.toLocaleString() : "-"}
                              </td>

                              <td className="buy-price">
                                {buy ? buy.price.toFixed(2) : "-"}
                              </td>

                              <td className="sell-price">
                                {sell ? sell.price.toFixed(2) : "-"}
                              </td>

                              <td className="sell-qty">
                                {sell ? sell?.quantity.toLocaleString() : "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    <div className="depth-summary">
                      <div className="depth-bar">
                        <div
                          className="buy-bar"
                          style={{ width: `${buyPercent}%` }}
                        />
                        <div
                          className="sell-bar"
                          style={{ width: `${sellPercent}%` }}
                        />
                      </div>

                      <div className="depth-text">
                        <span className="buy-text">
                          {buyPercent?.toFixed(2)}% Buy
                          <span
                            style={{
                              marginLeft: "5px",
                            }}
                          >{`(${totalBuyQty?.toLocaleString()})`}</span>
                        </span>

                        <span className="total-text">
                          Total Quantity{" "}
                          <strong style={{ marginLeft: "5px" }}>
                            {`(${totalQty.toLocaleString()})`}
                          </strong>
                        </span>

                        <span className="sell-text">
                          {sellPercent?.toFixed(2)}% Sell
                          <span
                            style={{
                              marginLeft: "5px",
                            }}
                          >{`(${totalSellQty?.toLocaleString()})`}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* <div className="factor-box">
                    <SmartMoney marketSnapshot={marketSnapshot} />
                  </div> */}

                  {/* macd crossober */}
                  <div className="factor-box">
                    <h4>
                      MACD Crossover{" "}
                      <InfoTooltip text={TOOLTIPS?.macdCrossover} />
                    </h4>
                    <table className="ma-table pivot-table">
                      <thead>
                        <tr>
                          {/* <th>Type</th> */}
                          <th>Date</th>
                          <th>Price</th>
                          <th>MACD</th>
                          <th>Signal</th>
                          <th>Message</th>
                        </tr>
                      </thead>

                      <tbody>
                        {[...crossovers]
                          .reverse()

                          .map((row, i) => (
                            <tr key={i}>
                              {/* <td>
                                <span
                                  style={{
                                    color:
                                      row.type === "BUY"
                                        ? "#22c55e"
                                        : "#ef4444",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {row.type}
                                </span>
                              </td> */}

                              <td>{row.date}</td>

                              <td>₹{row.price?.toFixed(2)}</td>

                              <td>{row.macd?.toFixed(2)}</td>

                              <td>{row.signal?.toFixed(2)}</td>

                              <td>
                                {row.type === "BUY"
                                  ? "MACD Bullish Crossover"
                                  : "MACD Bearish Crossover"}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Crossover percentage profit loss */}
                  {/* <div className="factor-box">
                    <table className="ma-table pivot-table">
                      <thead>
                        <tr>
                          <th>Entry</th>
                          <th>Buy</th>
                          <th>Exit</th>
                          <th>Sell</th>
                          <th>P/L %</th>
                          <th>Max Profit %</th>
                          <th>Status</th>
                        </tr>
                      </thead>

                      <tbody>
                        {trades.map((trade, idx) => (
                          <tr key={idx}>
                            <td>{trade.entryDate}</td>

                            <td>₹{trade.entryPrice?.toFixed(2)}</td>

                            <td>{trade.exitDate || "-"}</td>

                            <td>
                              {trade.exitPrice
                                ? `₹${trade.exitPrice.toFixed(2)}`
                                : "-"}
                            </td>

                            <td
                              style={{
                                color:
                                  Number(trade.pnl) >= 0
                                    ? "#22c55e"
                                    : "#ef4444",
                              }}
                            >
                              {trade.pnl}%
                            </td>

                            <td>{trade.maxProfit}%</td>

                            <td>{trade.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div> */}
                </div>
              </div>

              {/* macd chart */}
              {macdChartData && macdChartData.length > 0 && (
                <div className="macd-chart-graph">
                  <div className="chart-top-heading">
                    <h3>
                      Price, MACD Line, Signal{" "}
                      <InfoTooltip text={TOOLTIPS?.signalLine} /> & Histogram{" "}
                      <InfoTooltip text={TOOLTIPS?.histogram} />
                    </h3>
                    <div className="chart-select">
                      <label>Interval</label>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <select
                          name=""
                          id=""
                          value={timeFrame}
                          onChange={(e) => setTimeFrame(e.target.value)}
                          style={{
                            background: "inherit",

                            fontWeight: "600",
                            color: "black",

                            boxShadow: " 0 4px 12px rgba(0, 0, 0, 0.4)",
                            padding: "5px 7px",
                            borderRadius: "10px",
                          }}
                        >
                          <option value="min">Minute</option>
                          <option value="day">Day</option>
                        </select>
                        <select
                          value={interval}
                          onChange={(e) => setInterval(e.target.value)}
                          style={{
                            background: "inherit",
                            fontWeight: "600",
                            color: "black",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
                            padding: "5px 7px",
                            borderRadius: "10px",
                          }}
                        >
                          {timeFrame === "min" && (
                            <>
                              <option value="1m">1 Minute</option>
                              <option value="3m">3 Minutes</option>
                              <option value="5m">5 Minutes</option>
                            </>
                          )}

                          {timeFrame === "day" && (
                            <>
                              <option value="1d">1 Day</option>
                              <option value="1w">1 Week</option>
                              <option value="1mo">1 Month</option>
                              <option value="3mo">3 Months</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>
                  </div>

                  <h3 className="trend-head">
                    Macd Trend <InfoTooltip text={TOOLTIPS?.macdLine} />
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={selectedData} syncId="stock-sync">
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />

                      <XAxis
                        dataKey="date"
                        tickFormatter={(date, index) => {
                          if (!date) return "";

                          if (timeFrame === "min") {
                            if (typeof date === "string") {
                              const timePart = date?.split(",")[1]?.trim(); // "12:35:00 pm"
                              if (!timePart) return "";

                              const [hhmm] = timePart.split(":"); // "12"
                              const minute = timePart.split(":")[1]; // "35"

                              return index % 10 === 0
                                ? `${hhmm}:${minute}`
                                : "";
                            }
                            return "";
                          }
                          const d = new Date(date);
                          if (isNaN(d.getTime())) return "";
                          return index % 15 === 0
                            ? d?.toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                              })
                            : "";
                        }}
                        tick={{ fontWeight: "bold", fontSize: 10 }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />

                      <YAxis
                        tick={{
                          fontWeight: "bold",
                          fontSize: 10,
                        }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />

                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#1f2937",
                          border: "none",
                          color: "#fff",
                        }}
                        labelFormatter={(label) => {
                          try {
                            if (!label) return "";
                            const hasTime = label.includes(":");
                            const d = new Date(label.replace(" ", "T"));
                            if (isNaN(d.getTime())) return label;

                            if (hasTime) {
                              return d.toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              });
                            } else {
                              return d.toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              });
                            }
                          } catch (err) {
                            return label;
                          }
                        }}
                        formatter={(value, name) => {
                          if (value == null) return ["–", name];

                          return [
                            Number(value).toFixed(2), // 🔥 FIX DECIMALS
                            name === "MACD"
                              ? "MACD"
                              : name === "signal"
                                ? "Signal"
                                : "Histogram",
                          ];
                        }}
                        labelStyle={{ color: "#fff", fontWeight: "bold" }}
                        itemStyle={{ color: "#fff" }}
                      />

                      <Legend
                        wrapperStyle={{ fontWeight: "bold" }}
                        payload={[
                          {
                            value: "Histogram (Bullish)", // custom label
                            type: "square",
                            color: "#2fd18a", // green for positive
                          },
                          {
                            value: "Histogram (Bearish)",
                            type: "square",
                            color: "#f87171", // red for negative
                          },
                          {
                            value: "MACD",
                            type: "line",
                            color: "#2563eb", // blue line
                          },
                          {
                            value: "Signal",
                            type: "line",
                            color: "#f59e0b", // orange line
                          },
                        ]}
                      />

                      <Bar
                        dataKey="histogram"
                        name="Histogram"
                        barSize={20}
                        shape={(props) => {
                          const { x, y, width, height, value } = props;

                          const barHeight = Math.abs(height);

                          return (
                            <rect
                              x={x}
                              y={value >= 0 ? y : y - barHeight} // 🔥 key fix
                              width={width}
                              height={barHeight} // 🔥 absolute height
                              fill={value >= 0 ? "#2fd18a" : "#f87171"}
                            />
                          );
                        }}
                      />

                      <Line
                        type="monotone"
                        dataKey="MACD"
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={false}
                        name="MACD"
                      />

                      <Line
                        type="monotone"
                        dataKey="signal"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        dot={false}
                        name="Signal"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <h3 className="trend-head">
                    Price Trend <InfoTooltip text={TOOLTIPS?.priceChart} />
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart
                      data={selectedPriceData}
                      syncId="stock-sync" // 🔥 LINKS WITH MACD & RSI
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />

                      <XAxis
                        dataKey="date"
                        tickFormatter={(date, index) => {
                          if (!date) return "";

                          if (timeFrame === "min" && typeof date === "string") {
                            const timePart = date.split(",")[1]?.trim(); // "12:35:00 pm"
                            if (!timePart) return "";

                            const [hh, mm] = timePart.split(":");
                            return index % 10 === 0 ? `${hh}:${mm}` : "";
                          }

                          const d = new Date(date);
                          return index % 15 === 0
                            ? d.toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                              })
                            : "";
                        }}
                        tick={{ fontWeight: "bold", fontSize: 10 }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />

                      <YAxis
                        tick={{ fontWeight: "bold", fontSize: 10 }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                        domain={["auto", "auto"]}
                      />

                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#1f2937",
                          border: "none",
                          color: "#fff",
                        }}
                        labelFormatter={(label) => {
                          if (!label) return "";

                          const hasTime = label.includes(":");
                          const d = new Date(label.replace(" ", "T"));

                          if (isNaN(d.getTime())) return label;

                          return hasTime
                            ? d.toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })
                            : d.toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              });
                        }}
                        formatter={(value) => [
                          Number(value).toFixed(2), // value
                          "Price", // 🔥 rename Close Price → Price
                        ]}
                        labelStyle={{ color: "#fff", fontWeight: "bold" }}
                        itemStyle={{ color: "#fff" }}
                      />
                      <Legend
                        wrapperStyle={{ fontWeight: "bold" }}
                        payload={[
                          {
                            value: "Price",
                            type: "line",
                            color: "#22c55e", // same green as price line
                          },
                        ]}
                      />

                      <Line
                        type="monotone"
                        dataKey="close"
                        stroke="#22c55e"
                        strokeWidth={2}
                        dot={false}
                        name="Close Price"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>

                  <div className="candle-chart-container">
                    <div className="chart-legend">
                      <h3 className="trend-head">
                        Candles Trend{" "}
                        <InfoTooltip text={TOOLTIPS?.candlestickChart} />
                      </h3>
                      <div className="candles-color-map">
                        <span className="legend-item vwap">🟡 VWAP</span>
                        <span className="legend-item buy">🟢 BUY Signal</span>
                        <span className="legend-item sell">🔴 SELL Signal</span>
                      </div>
                    </div>
                    <CandleChart
                      intradayData={intradayVwap}
                      histData={indicatorData}
                      setLastUTSignal={setLastUTSignal}
                      chartIndicatorData={chartIndicatorData}
                      width={"100%"}
                    />
                  </div>
                </div>
              )}

              {/* News  */}

              {newsData?.body?.length > 0 && (
                <div className="factor-box">
                  <h4>News</h4>
                  <div className="news-grid stock-news-detail">
                    {newsData &&
                      newsData?.body
                        ?.slice(0, visibleCount)
                        ?.map((item, index) => (
                          <a
                            key={index}
                            href={item.link} // ✅ FIX
                            target="_blank"
                            rel="noopener noreferrer"
                            className="news-card"
                          >
                            <div className="news-content">
                              <h3 className="news-title">{item.title}</h3>

                              <div className="news-timestamp">
                                <p className="news-time">
                                  {new Date(item.pubDate).toLocaleString(
                                    "en-IN",
                                  )}
                                </p>

                                <p className="news-time">
                                  Source - {item.source}
                                </p>
                              </div>
                            </div>
                          </a>
                        ))}
                  </div>

                  {visibleCount < newsData?.body?.length && (
                    <div className="view-more-container">
                      <button
                        className="rec-btn"
                        onClick={() => setVisibleCount((prev) => prev + 4)}
                      >
                        View More
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* <ChatPanel2 stockContext={stockAISummaryPayload} /> */}
            <AIFloatButton />
          </div>
        )}
      </>

      {/* <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      /> */}
    </>
  );
};

export default StockDetailPage;
